import { useEffect } from 'react';
import type { CoteraApiV2, Whoami } from '../etc/api/typed-client';
import { useAuth } from '@workos-inc/authkit-react';
import {
  QueryClient,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';
import { useClient } from './use-client';
import { useGetBearerToken } from './use-bearer-token';
import { useAnalytics } from '@cotera/client/app/etc';
import { AnalyticsClient } from '../etc/metrics/analytics';
import { config } from '@cotera/client/config/config';
import { setupPylonIdentity } from '../etc/pylon';
import { clearStoredGclid, hashEmailForConversion } from '../etc/gclid';

/** AuthKit can set `user` before `getAccessToken()` is ready (common on impersonation boot). */
const AUTH_TOKEN_NOT_READY = 'auth-token-not-ready';
/** Impersonation sessions can 401 once before the impersonation JWT is fully active. */
const WHOAMI_AUTH_TRANSIENT = 'whoami-auth-transient';

// ---------------------------------------------------------------------------
// Pure async helpers (no React)
// ---------------------------------------------------------------------------

async function fireConversionEvent(email: string): Promise<void> {
  if (typeof window.gtag !== 'function') {
    return;
  }

  const hashedEmail = await hashEmailForConversion(email);
  if (hashedEmail !== null) {
    window.gtag('set', 'user_data', {
      sha256_email_address: hashedEmail,
    });
  }

  window.gtag('event', 'conversion', {
    send_to: 'AW-17507313406/Z7qFCLWxgOwbEP7NkZxB',
  });
}

async function switchWorkosSession(
  client: CoteraApiV2,
  switchToOrganization: (opts: { organizationId: string }) => Promise<unknown>
): Promise<void> {
  const whoamiRes = await client.u.whoami.get({});
  if (whoamiRes.isErr() || whoamiRes.value.orgs.length === 0) {
    return;
  }
  const newOrg = whoamiRes.value.orgs[0];
  if (newOrg?.workosId !== null && newOrg?.workosId !== undefined) {
    await switchToOrganization({ organizationId: newOrg.workosId });
  }
}

async function runOrgSetup(params: {
  whoami: Whoami;
  client: CoteraApiV2;
  analytics: AnalyticsClient;
  switchToOrganization: (opts: { organizationId: string }) => Promise<unknown>;
  queryClient: QueryClient;
}): Promise<void> {
  const { whoami, client, analytics, switchToOrganization, queryClient } =
    params;

  if (config.posthogKey) {
    analytics.alias(whoami.id, whoami.email);
    analytics.identifyById(whoami);
  }

  const result = await client.u.onboarding.submitOrgSetup({});

  if (result.isErr()) {
    throw new Error(result.error.err.errorType ?? 'Something went wrong');
  }

  await fireConversionEvent(whoami.email);
  clearStoredGclid();

  try {
    await switchWorkosSession(client, switchToOrganization);
  } catch {
    // switchToOrganization may reject if it redirected — that's fine
  }

  // Refetch whoami through react-query instead of hard reload
  await queryClient.invalidateQueries({ queryKey: ['whoami'] });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

type BootResult =
  | { status: 'loading'; retrying: boolean }
  | { status: 'error'; error: string; retry: () => void }
  | { status: 'logged-out' }
  | { status: 'unexpected-error' }
  | { status: 'ready'; whoami: Whoami };

export function useBootSequence(): BootResult {
  const {
    isLoading: authLoading,
    impersonator,
    user,
    switchToOrganization,
  } = useAuth();
  const client = useClient();
  const getBearerToken = useGetBearerToken();
  const queryClient = useQueryClient();
  const analytics = useAnalytics();

  const whoamiQ = useQuery({
    enabled: !authLoading && user !== null,
    queryFn: async () => {
      const bearerToken = await getBearerToken();
      if (bearerToken === undefined) {
        throw new Error(AUTH_TOKEN_NOT_READY);
      }

      const res = await client.u.whoami.get({});
      if (res.isOk()) {
        if (res.value.pylonVerificationHash !== null) {
          setupPylonIdentity({
            email: res.value.email,
            emailHash: res.value.pylonVerificationHash,
            name: res.value.name,
          });
        }
        return res;
      }
      if (
        res.error.err.errorType === 'Forbidden' ||
        res.error.err.errorType === 'Unauthorized'
      ) {
        // Impersonation boot can briefly 401 while the session JWT catches up.
        if (impersonator !== null && impersonator !== undefined) {
          throw new Error(WHOAMI_AUTH_TRANSIENT);
        }
        return res;
      }
      // Other errors throw so react-query retries with backoff
      throw new Error(res.error.err.errorType ?? 'whoami-failed');
    },
    queryKey: ['whoami', user?.id, impersonator?.email],
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message === AUTH_TOKEN_NOT_READY) {
        return failureCount < 5;
      }
      if (error instanceof Error && error.message === WHOAMI_AUTH_TRANSIENT) {
        return failureCount < 3;
      }
      return failureCount < 5;
    },
    retryDelay: (attempt) => Math.min(2000 * 2 ** attempt, 32_000),
  });

  const result = whoamiQ.data;

  const needsSetup =
    result !== undefined &&
    result.isOk() &&
    result.value.onboardingIncomplete === true;

  const setupMutation = useMutation({
    mutationFn: (whoami: Whoami) =>
      runOrgSetup({
        whoami,
        client,
        analytics,
        switchToOrganization,
        queryClient,
      }),
  });

  useEffect(() => {
    if (needsSetup && setupMutation.isIdle) {
      setupMutation.mutate(result!.value);
    }
  }, [needsSetup, setupMutation, result]);

  // Clear retry counter on success
  if (
    result !== undefined &&
    result.isOk() &&
    result.value.onboardingIncomplete !== true
  ) {
    sessionStorage.removeItem('cotera:whoami_retry_count');
  }

  // Still loading WorkOS auth
  if (authLoading) {
    return { status: 'loading', retrying: false };
  }

  // No WorkOS session — skip whoami and show login
  if (user === null) {
    return { status: 'logged-out' };
  }

  // Loading whoami (including retries)
  if (whoamiQ.isLoading) {
    return { status: 'loading', retrying: whoamiQ.failureCount > 0 };
  }

  // All whoami retries exhausted
  if (whoamiQ.isError) {
    return { status: 'unexpected-error' };
  }

  // Setup error
  if (setupMutation.isError) {
    return {
      status: 'error',
      error: setupMutation.error.message,
      retry: () => setupMutation.reset(),
    };
  }

  // Org setup in flight
  if (setupMutation.isPending || needsSetup) {
    return { status: 'loading', retrying: false };
  }

  if (result === undefined) {
    return { status: 'loading', retrying: false };
  }

  // Auth error -> logged out or unexpected
  if (result.isErr()) {
    if (
      result.error.err.errorType === 'Forbidden' ||
      result.error.err.errorType === 'Unauthorized'
    ) {
      return { status: 'logged-out' };
    }
    return { status: 'unexpected-error' };
  }

  return { status: 'ready', whoami: result.value };
}
